import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import theme from "../../theme";
import CommonSnackBar from "../snackbar";
import CommonTable from "../table";
import CommonSwitch from "../switch";
import {
  fetchFeatureFlagsRequest,
  updateFeatureFlagRequest,
} from "../../containers/featureFlags/actions";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsLoading,
} from "../../containers/featureFlags/selectors";
import { useDispatch, useSelector } from "react-redux";
import { userCanAccess } from "../../lib/permissions";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 175px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: "24px",
    marginBottom: "20px",
  },
  description: {
    color: theme.colors.DodgerBlue,
    marginBottom: "20px",
  },
  errorText: {
    color: theme.colors.BrightSun || "#f44336",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    marginTop: "12px",
  },
  emptyState: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    marginTop: "20px",
  },
}));

const formatFlagNameForDisplay = (name) => {
  if (!name) return "-";
  return name
    .toString()
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
};

const FEATURE_FLAG_UPDATE_NAME = "feature_flag_update";

function FeatureFlagsPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const items = useSelector(selectFeatureFlags);
  const loading = useSelector(selectFeatureFlagsLoading);
  const error = useSelector(selectFeatureFlagsError);
  const updatingByName = useSelector(
    (state) => state?.featureFlagsReducer?.updatingByName || {}
  );

  // Only users who can edit flags (is_super OR `feature_flags.edit`) may toggle them.
  const me = useSelector((state) => state?.loginReducer?.me);
  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    storedUser = null;
  }
  const currentUser = me || storedUser;
  const canEditFlags = userCanAccess(currentUser, "feature_flags.edit");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    variant: "info",
  });

  const pendingUpdatesRef = useRef({});

  useEffect(() => {
    dispatch(fetchFeatureFlagsRequest());
  }, [dispatch]);

  useEffect(() => {
    const pendingNames = Object.keys(pendingUpdatesRef.current);
    if (!pendingNames.length) return;

    pendingNames.forEach((name) => {
      if (updatingByName[name]) return;

      const expectedEnabled = pendingUpdatesRef.current[name];
      const flag = items.find((item) => item?.name === name);
      const success = flag && flag.is_enabled === expectedEnabled;

      setSnackbar({
        open: true,
        message: success
          ? `Feature flag "${name}" updated successfully.`
          : (error || `Failed to update feature flag "${name}".`),
        variant: success ? "success" : "error",
      });

      delete pendingUpdatesRef.current[name];
    });
  }, [updatingByName, items, error]);

  const handleToggle = (name, is_enabled) => {
    pendingUpdatesRef.current[name] = is_enabled;
    dispatch(updateFeatureFlagRequest({ name, is_enabled }));
  };

  const shouldRenderFlagsInUi = useMemo(() => {
    const renderControlFlag = items.find(
      (item) => item?.name === FEATURE_FLAG_UPDATE_NAME
    );
    if (!renderControlFlag) return true;
    return !!renderControlFlag?.is_enabled;
  }, [items]);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: (data) => formatFlagNameForDisplay(data),
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: (data) => data || "-",
      },
      {
        Header: "Enabled",
        accessor: "is_enabled",
        Cell: (isEnabled, row) => {
          const isUpdating = !!updatingByName?.[row?.name];
          return (
            <CommonSwitch
              name={row?.name}
              checked={!!isEnabled}
              onClick={(event) => handleToggle(row?.name, event.target.checked)}
              disabled={isUpdating || !canEditFlags}
            />
          );
        },
      },
      {
        Header: "Updated At",
        accessor: "updated_at",
        Cell: (data) => (data ? moment(data).format("lll") : "-"),
      },
    ],
    [handleToggle, updatingByName, canEditFlags]
  );

  return (
    <div className={classes.root}>
      <div className={classes.title}>Feature Flags</div>
      <div className={classes.description}>
        Manage backoffice feature behavior by enabling or disabling flags.
      </div>

      {!loading && error ? <div className={classes.errorText}>{error}</div> : null}

      {!error && shouldRenderFlagsInUi ? (
        <CommonTable columns={columns} data={items} loading={loading} fullRadius />
      ) : null}

      {!loading && !error && !shouldRenderFlagsInUi ? (
        <div className={classes.emptyState}>
          Feature flags are currently disabled for UI rendering.
        </div>
      ) : null}

      <CommonSnackBar
        open={snackbar.open}
        message={snackbar.message}
        variant={snackbar.variant}
        handleCloseStyle={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
      />
    </div>
  );
}

export default FeatureFlagsPage;
